import React, { useState } from "react";
import axios from "axios";
import { Button } from "react-bootstrap";
import LoaderButton from "./LoaderButton";
import {
  EnrolmentValues,
  MacularOedema,
  macularOedemas,
  RecallPeriod,
} from "./types";
import {
  renderYesNo,
  renderYesNoImages,
  renderMacularOedema,
  renderIndigenous,
  renderRecallPeriod,
  EnrolmentConfirmProps,
} from "./EnrolmentConfirm";

/*export const renderYesNo = (value: boolean | "true" | "false") =>
  value === true || value === "true" ? "Yes" : "No";

export const renderYesNoImages = (value: string) =>
  value ? "Image provided" : "No image";

export const renderMacularOedema = (value: MacularOedema) => macularOedemas[value];

export const renderIndigenous = (value: string) => {
  if (value === "yes") return "Yes";
  else if (value === "no") return "No";
  else if (value === "unknown") return "Unknown";
  else return "";
};

const renderRecallPeriod = (value: RecallPeriod) => {
  if (value === "1_year") return "1 year";
  else if (value === "2_years") return "2 years";
  else if (value === "6_months") return "6 months";
  else if (value === "less_6_months") return "Less than 6 months";
};

export type EnrolmentConfirmProps = {
  values: EnrolmentValues;
  csrfToken: string;
  renderNewEnrolmentForm: (values: any) => void;
  previousPage: () => void;
};*/

function EyeConnectEnrolmentConfirm({
  values: initialValues,
  csrfToken,
  previousPage,
  renderNewEnrolmentForm: renderNewEnrolmentFormCallback,
}: EnrolmentConfirmProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState(false);
  const [values] = useState(initialValues);

  let errorMessage: JSX.Element = <></>;
  if (serverError === true) {
    errorMessage = (
      <div>Something unexpected happened. Failed to submit enrolment.</div>
    );
  }

  const handleClick = async () => {
    const {
      practice_name,
      title,
      first_name,
      surname,
      dob,
      postcode,
      ndss,
      indigenous,
      patient_consent,
      visual_acuity_right,
      visual_acuity_right_normal_with_modifier,
      visual_acuity_left,
      visual_acuity_left_normal_with_modifier,
      diabetic_retinopathy_left,
      diabetic_retinopathy_right,
      macular_oedema_left,
      macular_oedema_right,
      hba1c,
      hba1c_unknown,
      last_eye_test,
      follow_up_sms,
      follow_up_sms_number,
      follow_up_email,
      follow_up_email_address,
      follow_up_post,
      follow_up_post_address,
      recall_months,
      date_of_fundus_exam,
      research_participant,
    } = values;

    let enrolment = {
      practice_name,
      title,
      first_name,
      surname,
      dob,
      postcode,
      ndss,
      indigenous,
      patient_consent,
      hba1c,
      hba1c_unknown,
      last_eye_test,
      follow_up_sms,
      follow_up_sms_number,
      follow_up_email,
      follow_up_email_address,
      follow_up_post,
      follow_up_post_address,
      recall_months,
      date_of_fundus_exam,
      research_participant,
    };

    if (values.research_participant) {
      enrolment = Object.assign(
        {
          visual_acuity_right: parseInt(visual_acuity_right, 10),
          visual_acuity_right_normal_with_modifier,
          visual_acuity_left: parseInt(visual_acuity_left, 10),
          visual_acuity_left_normal_with_modifier,
          diabetic_retinopathy_left,
          diabetic_retinopathy_right,
          macular_oedema_left,
          macular_oedema_right,
          left_eye_image: values.left_eye_image,
          right_eye_image: values.right_eye_image,
          left_oct_eye_image: values.left_oct_eye_image,
          right_oct_eye_image: values.right_oct_eye_image,
        },
        enrolment,
      );
    }

    const params = { enrolment };

    const config = { headers: { "X-CSRF-TOKEN": csrfToken } };

    try {
      setIsLoading(true);
      await axios.post("/eye_connect_enrolments", params, config);
    } catch (e) {
      setIsLoading(false);
      setServerError(true);
      return;
    }
    console.log();
    renderNewEnrolmentForm();
  };

  const renderSMSPreference = () => {
    if (!values.follow_up_sms) {
      return "";
    } else {
      return (
        <span>
          SMS: {values.follow_up_sms_number}
          <br />
        </span>
      );
    }
  };

  const renderEmailPreference = () => {
    if (!values.follow_up_email) {
      return "";
    } else {
      return (
        <span>
          Email: {values.follow_up_email_address}
          <br />
        </span>
      );
    }
  };

  const renderNewEnrolmentForm = () => {
    const { first_name, surname, dob } = values;
    const props = {
      previousEnrolment: {
        patientName: `${first_name} ${surname}`,
        patientDob: dob,
        //recallPeriod: renderRecallPeriod(recall_months),
      },
    };

    renderNewEnrolmentFormCallback(props);
  };

  const renderPostPreference = () => {
    if (!values.follow_up_post) {
      return "";
    } else {
      return (
        <span>
          Post: {values.follow_up_post_address}
          <br />
        </span>
      );
    }
  };

  const renderCommunicationPreference = () => {
    return (
      <div className="key-value-value" id="communication_preference">
        {renderSMSPreference()}
        {renderEmailPreference()}
        {renderPostPreference()}
      </div>
    );
  };

  const renderResearchParticipation = () => {
    if (!values.research_participant) return null;
    return (
      <div className="card-body">
        <div className="content-inner">
          <h4>Examination Findings</h4>

          <div className="key-value-wrapper">
            <div className="key-value">
              <div className="key-value-key"></div>
              <div className="key-value-header">Right</div>
              <div className="key-value-header">Left</div>
            </div>

            <div className="key-value">
              <div className="key-value-key">Visual acuity 6/6</div>
              <div className="key-value-value" id="visual_acuity_right">
                {values.visual_acuity_right}/
                {values.visual_acuity_right_normal_with_modifier}
              </div>
              <div className="key-value-value" id="visual_acuity_left">
                {values.visual_acuity_left}/
                {values.visual_acuity_left_normal_with_modifier}
              </div>
            </div>

            <div className="key-value">
              <div className="key-value-key">Diabetic retinopathy</div>
              <div className="key-value-value" id="diabetic_retinopathy_right">
                {values.diabetic_retinopathy_right}
              </div>
              <div className="key-value-value" id="diabetic_retinopathy_left">
                {values.diabetic_retinopathy_left}
              </div>
            </div>

            <div className="key-value">
              <div className="key-value-key">Macular oedema</div>
              <div className="key-value-value" id="macular_oedema_right">
                {renderMacularOedema(values.macular_oedema_right)}
              </div>
              <div className="key-value-value" id="macular_oedema_left">
                {renderMacularOedema(values.macular_oedema_left)}
              </div>
            </div>
          </div>

          <h4>Investigation</h4>

          <div className="key-value-wrapper">
            <div className="key-value">
              <div className="key-value-key"></div>
              <div className="key-value-header">Right</div>
              <div className="key-value-header">Left</div>
            </div>

            <div className="key-value">
              <div className="key-value-key">Retinal/Disc photo</div>
              <div className="key-value-value" id="right_eye_image">
                {renderYesNoImages(values.right_eye_image)}
              </div>
              <div className="key-value-value" id="left_eye_image">
                {renderYesNoImages(values.left_eye_image)}
              </div>
            </div>

            <div className="key-value">
              <div className="key-value-key">OCT scan</div>
              <div className="key-value-value" id="right_oct_eye_image">
                {renderYesNoImages(values.right_oct_eye_image)}
              </div>
              <div className="key-value-value" id="left_oct_eye_image">
                {renderYesNoImages(values.left_oct_eye_image)}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mdc-layout-grid max-width">
      <h1>Confirm Details</h1>

      {errorMessage}

      <div className="mdc-layout-grid__inner">
        <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-6">
          <div className="mdc-layout-grid__inner">
            <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-12">
              <div className="key-value-wrapper">
                <div className="key-value">
                  <div className="key-value-key">Practice name</div>
                  <div className="key-value-value" id="practice_name">
                    {values.practice_name}
                  </div>
                </div>
              </div>

              <h3>Personal Details</h3>

              <div className="key-value-wrapper">
                <div className="key-value">
                  <div className="key-value-key">Name</div>
                  <div className="key-value-value" id="name">
                    {values.title} {values.first_name} {values.surname}
                  </div>
                </div>

                <div className="key-value">
                  <div className="key-value-key">Date of Birth</div>
                  <div className="key-value-value" id="dob">
                    {values.dob}
                  </div>
                </div>

                <div className="key-value">
                  <div className="key-value-key">Postcode</div>
                  <div className="key-value-value" id="postcode">
                    {values.postcode}
                  </div>
                </div>

                <div className="key-value">
                  <div className="key-value-key">NDSS</div>
                  <div className="key-value-value" id="ndss">
                    {values.ndss}
                  </div>
                </div>

                <div className="key-value">
                  <div className="key-value-key">Indigenous</div>
                  <div className="key-value-value" id="indigenous">
                    {renderIndigenous(values.indigenous)}
                  </div>
                </div>

                <div className="key-value">
                  <div className="key-value-key">HbA1c</div>
                  <div className="key-value-value" id="hba1c">
                    {values.hba1c || "Unknown"}
                  </div>
                </div>
              </div>

              <h3>Eye Connect recall</h3>

              <div className="key-value-wrapper">
                <div className="key-value">
                  <div className="key-value-key">Communication preference</div>
                  <div className="key-value-value">
                    {renderCommunicationPreference()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-6">
          <div className="mdc-layout-grid__inner">
            <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-12">
              <div className="card">
                <div className="card-header">
                  <h3>Research participation</h3>
                  Participate in research{" "}
                  <b>{renderYesNo(values.research_participant)}</b>
                </div>

                {renderResearchParticipation()}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mdc-layout-grid__inner">
        <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-2">
          <LoaderButton
            id="submit_eye_connect_enrolment_btn"
            className="primary-button"
            variant="primary"
            size="lg"
            isLoading={isLoading}
            text="Send"
            loadingText="Sending…"
            onClick={handleClick}
          />
        </div>
        <div className="mdc-layout-grid__cell mdc-layout-grid__cell--span-3">
          <Button
            variant="primary"
            className="secondary-button"
            size="lg"
            onClick={previousPage}
          >
            Go back
          </Button>
        </div>
      </div>
    </div>
  );
}

export default EyeConnectEnrolmentConfirm;
